import React, { useEffect, useState, useRef } from "react";
import { ExcelTaskItem } from "../hooks/useExcelTaskPolling";
import { util } from "../config/util";
import ExcelFixGuideModal from "./ExcelFixGuideModal";
import { matchExcelFixGuide } from "./excelFixGuides";

interface ExcelTaskPanelProps {
  tasks: ExcelTaskItem[];
  onClear: (statusFilter?: string) => void;
  onCancel: (taskId: number) => void;
  onCancelAll: () => void;
  onDelete: (taskId: number) => void;
  onRetry: (taskId: number) => void;
}

const SERVICE_TYPE_LABELS: Record<string, string> = {
  x2: "두줄 나누기",
  contract: "계약내역서",
  ready: "기성내역서",
  delete: "한줄 만들기",
  color: "색상교체",
  override: "변경준공",
  fluctuation: "증감내역서",
  report: "실정보고서",
  fluctuation_report: "증감+실정보고서",
  phased: "차수분 내역서",
  document_contract: "계약서",
  document_construction: "시공계획서",
  document_completion: "준공계획서",
  document_billing: "청구서",
  document_safetyPlan: "안전관리비 계획서",
  document_environmentPlan: "환경보전비 계획서",
  document_safetyReport: "안전관리비 내역서",
  document_environmentReport: "환경보전비 내역서",
  document_directConstruction: "직접시공",
  document_prepayment: "선급금 신청서",
  document_statementExplain: "시방서",
};


/** 서버가 보고한 진행률·남은 시간 → " · 42% · 약 3분 남음". 정보 없으면 빈 문자열. */
function formatEta(task: ExcelTaskItem): string {
  const parts: string[] = [];
  if (typeof task.progress === "number" && task.progress > 0) parts.push(`${Math.round(task.progress * 100)}%`);
  const remain = task.estimated_remaining_seconds;
  if (typeof remain === "number" && remain >= 0) {
    if (remain < 60) parts.push("1분 미만 남음");
    else if (remain < 3600) parts.push(`약 ${Math.ceil(remain / 60)}분 남음`);
    else parts.push(`약 ${Math.floor(remain / 3600)}시간 ${Math.ceil((remain % 3600) / 60)}분 남음`);
  }
  return parts.length ? ` · ${parts.join(" · ")}` : "";
}

function getElapsedTime(createdAt: string | null): string {
  if (!createdAt) return "";
  const diff = Date.now() - new Date(createdAt).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}초`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}분`;
  const hours = Math.floor(minutes / 60);
  return `${hours}시간 ${minutes % 60}분`;
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = d.getHours().toString().padStart(2, "0");
  const minutes = d.getMinutes().toString().padStart(2, "0");
  return `${month}/${day} ${hours}:${minutes}`;
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function formatDuration(createdAt: string | null, updatedAt: string | null): string {
  if (!createdAt || !updatedAt) return "";
  const diff = new Date(updatedAt).getTime() - new Date(createdAt).getTime();
  if (diff < 0) return "";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}초 소요`;
  const minutes = Math.floor(seconds / 60);
  const remainSec = seconds % 60;
  if (minutes < 60) return remainSec > 0 ? `${minutes}분 ${remainSec}초 소요` : `${minutes}분 소요`;
  const hours = Math.floor(minutes / 60);
  return `${hours}시간 ${minutes % 60}분 소요`;
}

const STYLE_ID = "excel-task-panel-styles";

function ensureStyles() {
  const existing = document.getElementById(STYLE_ID);
  if (existing) existing.remove();
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes excelTaskSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes excelTaskPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.08); }
    }
    @keyframes excelTaskSlideUp {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes excelTaskProgress {
      0% { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    .excel-task-fab:hover {
      transform: scale(1.05) !important;
      box-shadow: 0 6px 20px rgba(33,150,243,0.35) !important;
    }
    .excel-task-download:hover {
      background-color: #1976D2 !important;
    }
    .excel-task-item:hover {
      background-color: #fafafa !important;
    }
    .excel-task-item:last-child {
      border-bottom: none !important;
    }
    .excel-task-action-btn:hover {
      background-color: #f0f0f0 !important;
    }
    .excel-task-btn {
      border: 1px solid #ddd;
      background: #fff;
      color: #666;
      font-size: 11px;
      font-weight: 500;
      padding: 3px 8px;
      border-radius: 4px;
      cursor: pointer;
      line-height: 1.2;
      transition: all 0.15s;
    }
    .excel-task-btn:hover {
      border-color: #bbb;
      color: #333;
    }
    .excel-task-btn-primary {
      border: none;
      background: #2196F3;
      color: #fff;
      font-size: 11px;
      font-weight: 600;
      padding: 3px 8px;
      border-radius: 4px;
      cursor: pointer;
      line-height: 1.2;
      transition: background-color 0.15s;
    }
    .excel-task-btn-primary:hover {
      background-color: #1976D2;
    }
    .excel-task-btn-danger {
      border: none;
      background: #f44336;
      color: #fff;
      font-size: 11px;
      font-weight: 600;
      padding: 3px 8px;
      border-radius: 4px;
      cursor: pointer;
      line-height: 1.2;
    }
    .excel-task-btn-danger:hover {
      background-color: #d32f2f;
    }
    .excel-task-btn-delete {
      border: 1px solid #e0e0e0;
      background: #fff;
      color: #999;
      font-size: 11px;
      padding: 3px 8px;
      border-radius: 4px;
      cursor: pointer;
      line-height: 1.2;
      transition: all 0.15s;
    }
    .excel-task-btn-delete:hover {
      border-color: #f44336;
      color: #f44336;
    }
    .excel-task-btn-guide {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      border: none;
      background: #FF8F00;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      padding: 3px 8px 3px 6px;
      border-radius: 10px;
      cursor: pointer;
      line-height: 1.2;
      white-space: nowrap;
      box-shadow: 0 1px 4px rgba(255,143,0,0.35);
      transition: background-color 0.15s, box-shadow 0.15s;
    }
    .excel-task-btn-guide:hover {
      background-color: #EF6C00;
      box-shadow: 0 2px 6px rgba(239,108,0,0.4);
    }
    .excel-task-filter-btn {
      border: 1px solid #e0e0e0;
      background: #fff;
      cursor: pointer;
      font-size: 11px;
      padding: 3px 8px;
      border-radius: 12px;
      color: #666;
      transition: all 0.15s;
    }
    .excel-task-filter-btn:hover {
      border-color: #2196F3;
      color: #2196F3;
    }
    .excel-task-filter-btn.active {
      background-color: #2196F3;
      border-color: #2196F3;
      color: #fff;
    }
    .excel-task-search {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 6px 10px 6px 30px;
      font-size: 12px;
      outline: none;
      width: 100%;
      transition: border-color 0.15s;
    }
    .excel-task-search:focus {
      border-color: #2196F3;
    }
    .excel-task-search::placeholder {
      color: #bbb;
    }
    .excel-task-filename-text {
      font-size: 12px;
      font-weight: 500;
      color: #1a1a1a;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      line-height: 1.4;
      word-break: break-all;
    }
  `;
  document.head.appendChild(style);
}

const SERVER_RESTART_MSG = "서버 재시작으로 인해 작업이 중단되었습니다.";

const FAILED_SEEN_KEY = "excel_failed_seen_at";

const ExcelTaskPanel: React.FC<ExcelTaskPanelProps> = ({ tasks, onClear, onCancel, onCancelAll, onDelete, onRetry }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [, setTick] = useState(0);
  const [search, setSearch] = useState("");
  // 패널을 펼친 마지막 시각 — 그 이후 실패만 "새 실패"로 카운트
  const [failedSeenAt, setFailedSeenAt] = useState<number>(() => {
    if (typeof window === "undefined") return 0;
    return +(localStorage.getItem(FAILED_SEEN_KEY) || 0);
  });
  const panelRef = useRef<HTMLDivElement>(null);
  // "조치 방법" 모달을 띄운 작업. 패널(isOpen) 바깥에 그려서 패널이 닫혀도 모달은 남는다.
  const [guideTask, setGuideTask] = useState<ExcelTaskItem | null>(null);

  const hasActive = tasks.some(
    (t) => t.status === "pending" || t.status === "processing"
  );
  const pendingCount = tasks.filter((t) => t.status === "pending").length;
  const processingCount = tasks.filter((t) => t.status === "processing").length;

  // 사용자가 마지막으로 패널을 펼친 이후 발생한 실패만 카운트
  const newFailedCount = tasks.filter(
    (t) =>
      (t.status === "failed" || t.status === "cancelled") &&
      t.updated_at &&
      +new Date(t.updated_at) > failedSeenAt
  ).length;

  // 필터 없음 — 검색만 적용
  const filteredTasks = tasks.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const fileName = (t.file_name || "").toLowerCase();
    const serviceLabel = (SERVICE_TYPE_LABELS[t.service_type] || t.service_type || "").toLowerCase();
    return fileName.includes(q) || serviceLabel.includes(q);
  });

  useEffect(() => ensureStyles(), []);

  useEffect(() => {
    if (!hasActive) return;
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, [hasActive]);

  // 새 task 추가되면 자동으로 패널 열기
  useEffect(() => {
    if (tasks.length > 0 && hasActive) setIsOpen(true);
  }, [tasks.length, hasActive]);

  // 패널을 펼치는 순간 — 그 시점까지의 실패는 "본 것"으로 간주
  useEffect(() => {
    if (isOpen) {
      const now = Date.now();
      localStorage.setItem(FAILED_SEEN_KEY, String(now));
      setFailedSeenAt(now);
    }
  }, [isOpen]);

  // 패널 바깥 클릭 시 닫기
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      // 조치 방법 모달은 body 포털에 떠 있어서 패널 밖 클릭으로 잡힌다 — 모달이 열려 있는 동안은 패널을 닫지 않는다.
      if (guideTask) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, guideTask]);

  if (tasks.length === 0) return null;

  const handleDownload = (fileUrl: string) => {
    const downloadUrl = fileUrl.replace(/\+/g, "%2B");
    util.downloadFile(downloadUrl);
  };

  // 플로팅 버튼 (FAB)
  // 우선순위: 진행중 > 새 실패 > 작업내역
  const fabLabel = hasActive
    ? processingCount > 0 && pendingCount > 0
      ? `처리중 ${processingCount} · 대기 ${pendingCount}`
      : processingCount > 0
      ? `처리중 ${processingCount}건`
      : `대기중 ${pendingCount}건`
    : newFailedCount > 0
    ? `${newFailedCount}건 실패`
    : `작업내역 ${tasks.length}건`;

  const fabBg = hasActive
    ? "#2196F3"
    : newFailedCount > 0
    ? "#f44336"
    : "#9e9e9e";  // 새 실패 없으면 회색 (덜 강조)

  return (
    <div ref={panelRef} style={{ position: "fixed", right: 24, top: 200, zIndex: 1000 }}>
      {/* 패널 (FAB 아래로 확장) */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: 48,
            right: 0,
            width: 280,
            maxHeight: 600,
            backgroundColor: "#fff",
            borderRadius: 16,
            boxShadow: "0 8px 40px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            animation: "excelTaskSlideUp 0.2s ease-out",
          }}
        >
          {/* 헤더 */}
          <div
            style={{
              padding: "14px 16px 10px",
              borderBottom: "1px solid #f0f0f0",
              background: "linear-gradient(135deg, #f8f9fa 0%, #fff 100%)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.3px" }}>
                작업내역
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                {hasActive && (
                  <button
                    onClick={onCancelAll}
                    style={{
                      border: "1px solid #f44336",
                      background: "none",
                      cursor: "pointer",
                      fontSize: 11,
                      color: "#f44336",
                      padding: "3px 8px",
                      borderRadius: 6,
                    }}
                    title="진행중인 작업 전체 취소"
                  >
                    전체 취소
                  </button>
                )}
                {!hasActive && tasks.length > 0 && (
                  <button
                    onClick={() => {
                      if (window.confirm("모든 작업내역이 삭제됩니다. 계속하시겠습니까?")) onClear("all");
                    }}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      color: "#999",
                      padding: "4px 8px",
                      borderRadius: 6,
                    }}
                    title="목록 비우기"
                  >
                    전체 비우기
                  </button>
                )}
              </div>
            </div>

            {/* 검색 */}
            <div style={{ position: "relative" }}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#bbb" }}
              >
                <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                className="excel-task-search"
                type="text"
                placeholder="파일명, 서비스 검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#999",
                    fontSize: 14,
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  x
                </button>
              )}
            </div>
          </div>

          {/* 진행 바 (active 있을 때) */}
          {hasActive && (
            <div
              style={{
                height: 3,
                background: "linear-gradient(90deg, #2196F3, #64B5F6, #2196F3)",
                backgroundSize: "200% 100%",
                animation: "excelTaskProgress 1.5s linear infinite",
              }}
            />
          )}

          {/* 작업 목록 */}
          <div style={{ overflowY: "auto", maxHeight: 440, padding: "4px 0" }}>
            {filteredTasks.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#999", fontSize: 13 }}>
                {search ? "검색 결과가 없습니다" : "해당 상태의 작업이 없습니다"}
              </div>
            ) : (
              filteredTasks.map((task, idx) => {
                const isPending = task.status === "pending";
                const isProcessing = task.status === "processing";
                const isActive = isPending || isProcessing;
                const isDone = task.status === "done";
                const isFailed = task.status === "failed";
                const isCancelled = task.status === "cancelled";

                const statusColor = isProcessing ? "#2196F3" : isPending ? "#FFA000" : isDone ? "#4CAF50" : isFailed ? "#f44336" : "#999";
                const etaText = isProcessing ? formatEta(task) : "";
                const statusText = isProcessing ? `처리중 ${getElapsedTime(task.created_at)}${etaText}` : isPending ? "대기중" : isDone ? "완료" : isFailed ? "실패" : "취소됨";

                const metaParts: string[] = [];
                metaParts.push(SERVICE_TYPE_LABELS[task.service_type] || task.service_type || "");
                if ((isDone || isFailed || isCancelled) && task.updated_at) metaParts.push(formatDateTime(task.updated_at));
                if (isDone && task.file_size) metaParts.push(formatFileSize(task.file_size));
                if (isDone && task.created_at && task.updated_at) {
                  const dur = formatDuration(task.created_at, task.updated_at);
                  if (dur) metaParts.push(dur);
                }

                return (
                  <div
                    key={task.id}
                    className="excel-task-item"
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #f5f5f5",
                      animation: `excelTaskSlideUp 0.2s ease-out ${idx * 0.03}s both`,
                      transition: "background-color 0.1s",
                    }}
                  >
                    {/* 1행: 상태 + 액션 버튼 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      {/* 상태 아이콘 + 텍스트 */}
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: statusColor }}>
                        {isProcessing ? (
                          <svg width="12" height="12" viewBox="0 0 14 14" style={{ animation: "excelTaskSpin 1s linear infinite" }}>
                            <circle cx="7" cy="7" r="5.5" fill="none" stroke={statusColor} strokeWidth="2" strokeDasharray="20 14" strokeLinecap="round" />
                          </svg>
                        ) : isPending ? (
                          <svg width="12" height="12" viewBox="0 0 14 14">
                            <circle cx="7" cy="7" r="5.5" fill="none" stroke={statusColor} strokeWidth="1.5" />
                            <path d="M7 4V7.5L9 9" fill="none" stroke={statusColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : isDone ? (
                          <svg width="12" height="12" viewBox="0 0 14 14">
                            <path d="M3 7.5L5.5 10L11 4" fill="none" stroke={statusColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 14 14">
                            <path d="M4 4L10 10M10 4L4 10" fill="none" stroke={statusColor} strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        )}
                        {statusText}
                      </span>

                      <div style={{ flex: 1 }} />

                      {/* 액션 버튼들 */}
                      {isActive && (
                        <button className="excel-task-btn" onClick={() => onCancel(task.id)}>취소</button>
                      )}
                      {/* 실패했거나, 완료됐지만 서버 안내문이 붙은 작업엔 조치 방법 안내 */}
                      {(isFailed || (isDone && !!task.error_message)) && (
                        <button
                          type="button"
                          className="excel-task-btn-guide"
                          onClick={() => setGuideTask(task)}
                          aria-label={`${task.file_name || "파일"} 조치 방법 보기`}
                          title="왜 실패했고 어떻게 고치는지 안내"
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M9 18h6M10 21h4" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
                            <path d="M12 3a6.5 6.5 0 0 0-3.9 11.7c.6.5.9 1.1.9 1.8V17h6v-.5c0-.7.3-1.3.9-1.8A6.5 6.5 0 0 0 12 3z" stroke="#fff" strokeWidth="2.2" strokeLinejoin="round" />
                          </svg>
                          조치 방법
                        </button>
                      )}
                      {isDone && task.file_url && (
                        <button className="excel-task-btn-primary" onClick={() => handleDownload(task.file_url!)}>저장</button>
                      )}
                      {isFailed && task.error_message === SERVER_RESTART_MSG && (
                        <button className="excel-task-btn-danger" onClick={() => onRetry(task.id)}>재시도</button>
                      )}
                      {!isActive && (
                        <button className="excel-task-btn-delete" onClick={() => { if (window.confirm("이 작업을 삭제하시겠습니까?")) onDelete(task.id); }}>삭제</button>
                      )}
                    </div>

                    {/* 2행: 파일명 (2줄) */}
                    <div className="excel-task-filename-text" title={task.file_name || ""}>
                      {task.file_name || "파일"}
                    </div>

                    {/* 3행: 메타 정보 */}
                    <div style={{ fontSize: 10, color: "#aaa", marginTop: 3, display: "flex", flexWrap: "wrap" as const, gap: 2 }}>
                      {metaParts.filter(Boolean).map((part, i) => (
                        <span key={i}>
                          {i > 0 && <span style={{ margin: "0 3px" }}>·</span>}
                          {part}
                        </span>
                      ))}
                    </div>

                    {/* 에러 메시지 — 실패는 빨강, 완료인데 안내문이 붙은 경우는 주황 */}
                    {(isFailed || isDone) && task.error_message && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 10,
                          color: isFailed ? "#e53935" : "#EF6C00",
                          lineHeight: "1.3",
                          backgroundColor: isFailed ? "#FFF5F5" : "#FFF8E1",
                          padding: "3px 6px",
                          borderRadius: 4,
                        }}
                        title={task.error_message}
                      >
                        {task.error_message.length > 60
                          ? task.error_message.substring(0, 60) + "..."
                          : task.error_message}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* 조치 방법 모달 — body 포털. 패널이 닫혀도 유지된다. */}
      {guideTask && (
        <ExcelFixGuideModal
          task={guideTask}
          guide={matchExcelFixGuide(guideTask.error_message)}
          serviceLabel={SERVICE_TYPE_LABELS[guideTask.service_type] || guideTask.service_type || ""}
          onClose={() => setGuideTask(null)}
          onRetry={() => onRetry(guideTask.id)}
        />
      )}

      {/* FAB 트리거 버튼 */}
      <button
        className="excel-task-fab"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          backgroundColor: fabBg,
          color: "#fff",
          border: "none",
          borderRadius: 50,
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 600,
          boxShadow: `0 4px 16px ${fabBg}44`,
          transition: "all 0.2s ease",
          animation: hasActive ? "excelTaskPulse 2s ease-in-out infinite" : "none",
          whiteSpace: "nowrap" as const,
        }}
      >
        {/* 아이콘 */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="2" y="1" width="12" height="14" rx="2" stroke="#fff" strokeWidth="1.5" />
          <path d="M5 5h6M5 8h6M5 11h3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        {fabLabel}
        {isOpen ? (
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path d="M2 7L5 4L8 7" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path d="M2 4L5 7L8 4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default ExcelTaskPanel;
